<template>
  <div class="about-page">
    <!-- Hero Section -->
    <section class="hero">
      <div class="hero-container">
        <div class="hero-content">
          <h1 class="hero-title">Transform Your School's Club Experience</h1>
          <p class="hero-subtitle">
            eClub is a comprehensive platform designed to streamline and enhance
            extracurricular activities for schools, students, and administrators.
          </p>
          <div class="hero-actions">
            <router-link to="/login" class="btn btn-primary">Get Started</router-link>
            <router-link to="/official/contact" class="btn btn-secondary">Contact Us</router-link>
          </div>
        </div>
        <div class="hero-image">
          <div class="dashboard-preview">
            <div class="preview-card">
              <h3>My Clubs Dashboard</h3>
              <p>Manage all your club activities in one place</p>
              <div class="feature-tags">
                <span class="feature-tag tag-events">Events</span>
                <span class="feature-tag tag-posts">Posts</span>
                <span class="feature-tag tag-todos">Todos</span>
                <span class="feature-tag tag-leadership">Leadership</span>
                <span class="feature-tag tag-members">Club Members</span>
                <span class="feature-tag tag-documents">Documents</span>
                <span class="feature-tag tag-profile">Profile</span>
                <span class="feature-tag tag-statistics">Statistics</span>
                <span class="feature-tag tag-achievements">Internal Achievements</span>
                <span class="feature-tag tag-honors">External Honors</span>
                <span class="feature-tag tag-content">Content Orders</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Features Overview -->
    <section class="features-overview">
      <div class="container">
        <h2 class="section-title">Powerful Features for Modern Club Management</h2>
        <div class="features-grid">
          <div class="feature-card">
            <div class="feature-icon">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M2 17L12 22L22 17" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M2 12L12 17L22 12" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <h3>Club Management</h3>
            <p>Comprehensive tools for organizing and managing school clubs with dedicated spaces for each activity.</p>
          </div>
          <div class="feature-card">
            <div class="feature-icon">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M21 15C21 15.5304 20.7893 16.0391 20.4142 16.4142C20.0391 16.7893 19.5304 17 19 17H7L3 21V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H19C19.5304 3 20.0391 3.21071 20.4142 3.58579C20.7893 3.96086 21 4.46957 21 5V15Z" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <h3>Discussion Forums</h3>
            <p>Organized conversations with focused topics, enabling clubs to maintain dedicated threads for various activities.</p>
          </div>
          <div class="feature-card">
            <div class="feature-icon">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M22 11.08V12C21.9988 14.1564 21.3005 16.2547 20.0093 17.9818C18.7182 19.709 16.9033 20.9725 14.8354 21.5839C12.7674 22.1953 10.5573 22.1219 8.53447 21.3746C6.51168 20.6273 4.78465 19.2461 3.61096 17.4371C2.43727 15.628 1.87979 13.4881 2.02168 11.3363C2.16356 9.18455 2.99721 7.13631 4.39828 5.49706C5.79935 3.85781 7.69279 2.71537 9.79619 2.24013C11.8996 1.76489 14.1003 1.98232 16.07 2.86" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M22 4L12 14.01L9 11.01" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <h3>Task Management</h3>
            <p>Streamlined todo features allowing clubs to create lists, assign responsibilities, and set deadlines.</p>
          </div>
          <div class="feature-card">
            <div class="feature-icon">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M4 19.5C4 18.837 4.26339 18.2011 4.73223 17.7322C5.20107 17.2634 5.83696 17 6.5 17H20" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M6.5 2H20V22H6.5C5.83696 22 5.20107 21.7366 4.73223 21.2678C4.26339 20.7989 4 20.163 4 19.5V4.5C4 3.83696 4.26339 3.20107 4.73223 2.73223C5.20107 2.26339 5.83696 2 6.5 2Z" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <h3>Interactive eBooks</h3>
            <p>Card-based learning with interactive elements, quizzes, and feedback for self-directed education.</p>
          </div>
        </div>
      </div>
    </section>


    <!-- E-Words Auto-Swiping Carousel Section -->
    <section class="e-words-section">
      <div class="e-words-carousel">
        <div class="e-words-wrapper" ref="carouselWrapper">
          <!-- Duplicate last slide at the beginning for infinite loop -->
          <div class="e-word-slide" :key="'duplicate-last'">
            <h3>{{ eWords[eWords.length - 1].title }}</h3>
            <p>{{ eWords[eWords.length - 1].description }}</p>
          </div>
          <!-- Real slides -->
          <div class="e-word-slide" v-for="(word, index) in eWords" :key="index">
            <h3>{{ word.title }}</h3>
            <p>{{ word.description }}</p>
          </div>
          <!-- Duplicate first slide at the end for infinite loop -->
          <div class="e-word-slide" :key="'duplicate-first'">
            <h3>{{ eWords[0].title }}</h3>
            <p>{{ eWords[0].description }}</p>
          </div>
        </div>
        <div class="carousel-indicators">
          <span 
            v-for="(word, index) in eWords" 
            :key="index"
            :class="['indicator', { active: currentSlide === index }]"
            @click="goToSlide(index)"
          ></span>
        </div>
      </div>
    </section>

    <!-- Mission Section -->
    <section class="mission">
      <div class="container">
        <div class="mission-content">
          <div class="mission-text">
            <h2>Our Mission</h2>
            <p>
              eClub is dedicated to transforming how educational institutions manage and enhance
              their extracurricular activities. We believe that well-organized club activities
              are essential for student development, fostering leadership, creativity, and
              collaborative skills that extend far beyond the classroom.
            </p>
            <p>
              Our platform bridges the gap between traditional club management and modern
              digital solutions, providing schools with the tools they need to create
              engaging, educational, and efficiently managed club experiences.
            </p>
          </div>
          <div class="mission-visual">
            <div class="visual-card">
              <h3>🎯 Vision</h3>
              <p>A world where every student has access to engaging, well-managed extracurricular opportunities.</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- User Types Section -->
    <section class="user-types">
      <div class="container">
        <h2 class="section-title">Built for Everyone in Your School</h2>
        <div class="types-grid">
          <div class="type-card">
            <h3>Students</h3>
            <ul>
              <li>Join and manage club memberships</li>
              <li>Participate in discussions</li>
              <li>Access interactive learning materials</li>
              <li>Track tasks and achievements</li>
            </ul>
          </div>
          <div class="type-card">
            <h3>Advisors</h3>
            <ul>
              <li>Manage multiple clubs efficiently</li>
              <li>Monitor student engagement</li>
              <li>Organize club activities and events</li>
              <li>Access comprehensive reporting</li>
            </ul>
          </div>
          <div class="type-card">
            <h3>School Admins</h3>
            <ul>
              <li>Oversee all school club activities</li>
              <li>Allocate clubs to teachers</li>
              <li>Generate annual reports</li>
              <li>Monitor global club performance</li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <!-- Platform Benefits -->
    <section class="benefits">
      <div class="container">
        <h2 class="section-title">Why Choose eClub?</h2>
        <div class="benefits-grid">

          <div class="benefit-card">
            <h3>Comprehensive Solution</h3>
            <p>
              From basic club registration to advanced analytics and reporting, eClub provides
              a complete ecosystem for club management that grows with your institution.
            </p>
          </div>

          <div class="benefit-card">
            <h3>User-Centric Design</h3>
            <p>
              Intuitive interfaces designed specifically for students, advisors, and administrators,
              ensuring everyone can use the platform effectively regardless of technical expertise.
            </p>
          </div>

          <div class="benefit-card">
            <h3>Scalable Platform</h3>
            <p>
              Whether you're a small school with a few clubs or a large institution with hundreds
              of activities, eClub scales to meet your needs.
            </p>
          </div>

          <div class="benefit-card">
            <h3>Academic Focus</h3>
            <p>
              Every feature supports educational goals, promoting skill development, leadership
              opportunities, and meaningful student engagement.
            </p>
          </div>

          <div class="benefit-card">
            <h3>Data-Driven Insights</h3>
            <p>
              Comprehensive analytics help schools understand participation patterns, measure
              engagement, and optimize their extracurricular programs.
            </p>
          </div>

          <div class="benefit-card">
            <h3>Continuous Support</h3>
            <p>
              Dedicated support team and extensive documentation ensure smooth implementation
              and ongoing success for your institution.
            </p>
          </div>

        </div>
      </div>
    </section>

    <!-- Impact Section -->
    <section class="impact">
      <div class="container">
        <!-- <h2 class="section-title">Our Impact</h2>
        <div class="stats-grid">
          <div class="stat-item">
            <h3>500+</h3>
            <p>Active Schools</p>
          </div>
          <div class="stat-item">
            <h3>10,000+</h3>
            <p>Students Engaged</p>
          </div>
          <div class="stat-item">
            <h3>2,000+</h3>
            <p>Active Clubs</p>
          </div>
          <div class="stat-item">
            <h3>98%</h3>
            <p>Satisfaction Rate</p>
          </div>
        </div> -->

        <div class="impact-story">
          <h3>Transforming Education Together</h3>
          <p>
            eClub is designed to facilitate meaningful learning opportunities,
            helping students develop leadership skills, pursue their passions, and build
            lasting connections. Our platform aims to help schools increase student
            participation in extracurricular activities while
            reducing administrative overhead, allowing educators to focus on what matters most - student growth and engagement.
          </p>
        </div>
      </div>
    </section>

    <!-- Technology Section -->
    <section class="technology">
      <div class="container">
        <h2 class="section-title">Built for the Future</h2>
        <div class="tech-content">
          <div class="tech-text">
            <h3>Modern Technology Stack</h3>
            <p>
              eClub is built using cutting-edge web technologies to ensure reliability,
              security, and performance. Our cloud-based architecture provides seamless
              scalability and accessibility from any device, anywhere in the world.
            </p>

            <h3>Security & Privacy</h3>
            <p>
              We take data protection seriously. eClub employs enterprise-grade security
              measures, including encryption, secure authentication, and regular security
              audits to protect your institution's and students' information.
            </p>

            <h3>Continuous Innovation</h3>
            <p>
              Our development team continuously updates and improves the platform based
              on user feedback and emerging educational technology trends, ensuring eClub
              remains at the forefront of club management solutions.
            </p>
          </div>
          <div class="tech-features">
            <ul>
              <li>✓ Cloud-based infrastructure</li>
              <li>✓ Mobile-responsive design</li>
              <li>✓ Real-time notifications</li>
              <li>✓ Advanced analytics</li>
              <li>✓ API integrations</li>
              <li>✓ Automated backups</li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="cta">
      <div class="container">
        <div class="cta-content">
          <h2>Ready to Transform Your School's Club Experience?</h2>
          <p>
            Join hundreds of institutions already using eClub to enhance their extracurricular programs
            and create engaging learning experiences for students.
          </p>
          <div class="cta-actions">
            <router-link to="/login" class="btn btn-primary">Get Started Today</router-link>
            <router-link to="/official/contact" class="btn btn-secondary">Contact Us</router-link>
          </div>
        </div>
      </div>
    </section>

  </div>
</template>

<script>
export default {
  name: 'OfficialAbout',
  data() {
    return {
      currentSlide: 0, // This tracks the actual slide index (0-12) for indicators
      actualPosition: 1, // This tracks the position in carousel (1 = first real slide, after duplicate last)
      autoSlideInterval: null,
      isTransitioning: false,
      eWords: [
        {
          title: 'E is for Efficient',
          description: 'Streamlining sign-ups, scheduling, and communication.'
        },
        {
          title: 'E is for Engaging',
          description: 'Creating dynamic, interactive spaces for every student.'
        },
        {
          title: 'E is for Empowering',
          description: 'Putting club management and participation in the hands of students and leaders.'
        },
        {
          title: 'E is for Essential',
          description: 'The central, must-have hub for all school club activity.'
        },
        {
          title: 'E is for Effortless',
          description: 'Reducing administrative work for staff and advisors.'
        },
        {
          title: 'E is for Expansive',
          description: 'Breaking down geographical and logistical limits for club access.'
        },
        {
          title: 'E is for Enriched',
          description: 'Deepening the club experience through seamless organization and resources.'
        },
        {
          title: 'E is for Energetic',
          description: 'Capturing the vibrant, active spirit of school clubs.'
        },
        {
          title: 'E is for Electronic',
          description: 'The digital foundation of a modern club ecosystem.'
        },
        {
          title: 'E is for Equitable',
          description: 'Ensuring every student has equal opportunity to discover and join clubs.'
        },
        {
          title: 'E is for Excellence',
          description: 'Elevating club standards and achievements through superior management.'
        },
        {
          title: 'E is for Evolved',
          description: 'Representing the next evolution in school club administration.'
        },
        {
          title: 'E is for Engine',
          description: "The driving force that transforms traditional club management into a unified, dynamic experience."
        }
      ]
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.updateCarouselPosition()
      this.startAutoSlide()
    })
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    this.stopAutoSlide()
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    startAutoSlide() {
      this.autoSlideInterval = setInterval(() => {
        this.nextSlide()
      }, 4000) // Change slide every 4 seconds
    },
    stopAutoSlide() {
      if (this.autoSlideInterval) {
        clearInterval(this.autoSlideInterval)
        this.autoSlideInterval = null
      }
    },
    nextSlide() {
      if (this.isTransitioning) return
      
      this.currentSlide = (this.currentSlide + 1) % this.eWords.length
      this.actualPosition++
      
      // If we've reached the duplicate first slide (position = eWords.length + 1)
      if (this.actualPosition === this.eWords.length + 1) {
        // After transition, jump to real first slide without animation
        setTimeout(() => {
          this.isTransitioning = true
          const wrapper = this.$refs.carouselWrapper
          if (wrapper) {
            wrapper.style.transition = 'none'
            this.actualPosition = 1
            this.currentSlide = 0
            this.updateCarouselPosition()
            setTimeout(() => {
              wrapper.style.transition = 'transform 0.6s ease-in-out'
              this.isTransitioning = false
            }, 50)
          }
        }, 600)
      }
      
      this.updateCarouselPosition()
    },
    goToSlide(index) {
      if (this.isTransitioning) return
      
      this.currentSlide = index
      this.actualPosition = index + 1 // +1 because of duplicate last at beginning
      this.updateCarouselPosition()
      // Restart auto-slide after manual navigation
      this.stopAutoSlide()
      this.startAutoSlide()
    },
    updateCarouselPosition() {
      this.$nextTick(() => {
        const wrapper = this.$refs.carouselWrapper
        if (wrapper) {
          const carousel = wrapper.parentElement
          if (carousel) {
            const carouselWidth = carousel.offsetWidth
            const totalSlides = this.eWords.length + 2 // +2 for duplicate slides
            // Set wrapper width to total width of all slides (including duplicates)
            wrapper.style.width = `${totalSlides * carouselWidth}px`
            // Set each slide to exactly carousel width
            const slides = wrapper.querySelectorAll('.e-word-slide')
            slides.forEach(slide => {
              slide.style.width = `${carouselWidth}px`
              slide.style.minWidth = `${carouselWidth}px`
              slide.style.maxWidth = `${carouselWidth}px`
            })
            // Update transform position - actualPosition accounts for duplicate last at start
            wrapper.style.transform = `translateX(-${this.actualPosition * carouselWidth}px)`
          }
        }
      })
    },
    handleResize() {
      this.updateCarouselPosition()
    }
  }
}
</script>

<style scoped lang="scss">
.about-page {
  .hero {
    background: linear-gradient(135deg, #fff5f3 0%, #ffe6e3 100%);
    padding: 80px 0;

    .hero-container {
      max-width: 1200px;
      margin: 0 auto;
      padding: 0 20px;
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 60px;
      align-items: center;
    }

    .hero-title {
      font-size: 3.5rem;
      font-weight: 700;
      color: #1a1a1a;
      margin-bottom: 20px;
      line-height: 1.2;
    }

    .hero-subtitle {
      font-size: 1.25rem;
      color: #666;
      margin-bottom: 30px;
      line-height: 1.6;
    }

    .hero-actions {
      display: flex;
      gap: 20px;
    }

    .dashboard-preview {
      background: white;
      border-radius: 15px;
      padding: 40px;
      box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);

      .preview-card {
        text-align: center;

        h3 {
          color: #FF6B4A;
          margin-bottom: 15px;
          font-size: 1.5rem;
        }

        p {
          color: #666;
          font-size: 1.1rem;
          margin-bottom: 20px;
        }

        .feature-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          justify-content: center;
          margin-top: 20px;

          .feature-tag {
            padding: 8px 16px;
            border-radius: 20px;
            font-size: 0.9rem;
            font-weight: 500;
            color: white;
            transition: all 0.3s ease;
            cursor: default;

            &:hover {
              transform: translateY(-2px);
              box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
            }

            &.tag-events {
              background: linear-gradient(135deg, #2196F3, #1976D2);
            }

            &.tag-posts {
              background: linear-gradient(135deg, #9C27B0, #7B1FA2);
            }

            &.tag-todos {
              background: linear-gradient(135deg, #4CAF50, #388E3C);
            }

            &.tag-leadership {
              background: linear-gradient(135deg, #FF9800, #F57C00);
            }

            &.tag-members {
              background: linear-gradient(135deg, #00BCD4, #0097A7);
            }

            &.tag-documents {
              background: linear-gradient(135deg, #673AB7, #512DA8);
            }

            &.tag-profile {
              background: linear-gradient(135deg, #E91E63, #C2185B);
            }

            &.tag-statistics {
              background: linear-gradient(135deg, #607D8B, #455A64);
            }

            &.tag-achievements {
              background: linear-gradient(135deg, #FFC107, #FFA000);
            }

            &.tag-honors {
              background: linear-gradient(135deg, #FF5722, #E64A19);
            }

            &.tag-content {
              background: linear-gradient(135deg, #3F51B5, #303F9F);
            }
          }
        }
      }
    }
  }

  .features-overview {
    padding: 80px 0;
    background: white;

    .section-title {
      text-align: center !important;
      width: 100%;
      display: block;
      margin: 0 auto 20px;
    }

    .features-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
      gap: 40px;
      margin-top: 50px;
    }

    .feature-card {
      text-align: center;
      padding: 30px 20px;
      border-radius: 15px;
      transition: transform 0.3s ease, box-shadow 0.3s ease;

      &:hover {
        transform: translateY(-5px);
        box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
      }

      .feature-icon {
        width: 80px;
        height: 80px;
        background: linear-gradient(45deg, #FF6B4A, #ff8a6b);
        border-radius: 50%;
        margin: 0 auto 20px;
        display: flex;
        align-items: center;
        justify-content: center;

        svg {
          width: 40px;
          height: 40px;
        }
      }

      h3 {
        color: #1a1a1a;
        margin-bottom: 15px;
        font-size: 1.5rem;
      }

      p {
        color: #666;
        line-height: 1.6;
      }
    }
  }

  .e-words-section {
    padding: 20px 0;
    background: linear-gradient(135deg, #fff5f3 0%, #ffe6e3 100%);

    .e-words-carousel {
      position: relative;
      max-width: 1000px;
      margin: 0 auto;
      overflow: hidden;
      padding: 0 20px;
    }

    .e-words-wrapper {
      display: flex;
      transition: transform 0.6s ease-in-out;
      will-change: transform;
    }

    .e-word-slide {
      flex: 0 0 auto;
      width: 100%;
      box-sizing: border-box;
      padding: 15px 20px;
      min-height: 120px;
      display: flex;
      flex-direction: column;
      justify-content: center;
      text-align: center;
      position: relative;

      h3 {
        color: #FF6B4A;
        font-size: 4rem;
        font-weight: 700;
        margin-bottom: 8px;
        line-height: 1.2;
      }

      p {
        color: #333;
        font-size: 1.8rem;
        line-height: 1.5;
        margin: 0;
        max-width: 900px;
        margin: 0 auto;
        font-weight: 400;
      }
    }

    .carousel-indicators {
      display: flex;
      justify-content: center;
      gap: 12px;
      margin-top: 30px;

      .indicator {
        width: 12px;
        height: 12px;
        border-radius: 50%;
        background: rgba(255, 107, 74, 0.3);
        cursor: pointer;
        transition: all 0.3s ease;

        &:hover {
          background: rgba(255, 107, 74, 0.6);
        }

        &.active {
          background: #FF6B4A;
          width: 32px;
          border-radius: 6px;
        }
      }
    }
  }

  .mission {
    padding: 80px 0;
    background: #f8f9fa;

    .mission-content {
      display: grid;
      grid-template-columns: 2fr 1fr;
      gap: 60px;
      align-items: center;
    }

    .mission-text {
      h2 {
        color: #1a1a1a;
        font-size: 2.5rem;
        margin-bottom: 30px;
        font-weight: 600;
      }

      p {
        color: #666;
        font-size: 1.1rem;
        line-height: 1.7;
        margin-bottom: 20px;
      }
    }

    .visual-card {
      background: linear-gradient(135deg, #FF6B4A, #ff8a6b);
      color: white;
      padding: 40px 30px;
      border-radius: 20px;
      text-align: center;

      h3 {
        font-size: 1.5rem;
        margin-bottom: 15px;
      }

      p {
        font-size: 1rem;
        line-height: 1.6;
        opacity: 0.9;
      }
    }
  }

  .user-types {
    padding: 80px 0;
    background: white;

    .section-title {
      text-align: center !important;
      width: 100%;
      display: block;
      margin: 0 auto 20px;
    }

    .types-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
      gap: 40px;
      margin-top: 50px;
    }

    .type-card {
      background: #f8f9fa;
      padding: 40px 30px;
      border-radius: 15px;
      box-shadow: 0 10px 20px rgba(0, 0, 0, 0.05);

      h3 {
        color: #FF6B4A;
        font-size: 1.8rem;
        margin-bottom: 25px;
        font-weight: 600;
      }

      ul {
        list-style: none;
        padding: 0;
        margin: 0;

        li {
          color: #666;
          margin-bottom: 12px;
          padding-left: 20px;
          position: relative;

          &::before {
            content: '✓';
            color: #FF6B4A;
            font-weight: bold;
            position: absolute;
            left: 0;
          }
        }
      }
    }
  }

  .benefits {
    padding: 80px 0;
    background: #f8f9fa;

    .section-title {
      text-align: center !important;
      width: 100%;
      display: block;
      margin: 0 auto 20px;
    }

    .benefits-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
      gap: 30px;
      margin-top: 50px;
    }

    .benefit-card {
      background: white;
      padding: 30px;
      border: 1px solid #e9ecef;
      border-radius: 12px;
      transition: all 0.3s ease;

      &:hover {
        border-color: #FF6B4A;
        box-shadow: 0 5px 15px rgba(255, 107, 74, 0.1);
      }

      h3 {
        color: #1a1a1a;
        font-size: 1.3rem;
        margin-bottom: 15px;
        font-weight: 600;
      }

      p {
        color: #666;
        line-height: 1.6;
      }
    }
  }

  .impact {
    padding: 80px 0;
    background: #1a1a1a;
    color: white;
    text-align: center;

    .section-title {
      text-align: center !important;
      width: 100%;
      display: block;
      margin: 0 auto 20px;
      color: white;
    }

    .stats-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      gap: 40px;
      margin-top: 50px;
    }

    .stat-item {
      h3 {
        font-size: 3rem;
        color: #FF6B4A;
        margin-bottom: 10px;
        font-weight: 700;
      }

      p {
        color: #ccc;
        font-size: 1.1rem;
      }
    }

    .impact-story {
      max-width: 800px;
      margin: 60px auto 0;

      h3 {
        color: #FF6B4A;
        font-size: 2rem;
        margin-bottom: 25px;
        font-weight: 600;
      }

      p {
        color: #ccc;
        font-size: 1.1rem;
        line-height: 1.7;
      }
    }
  }

  .technology {
    padding: 80px 0;
    background: white;

    .section-title {
      text-align: center !important;
      width: 100%;
      display: block;
      margin: 0 auto 20px;
    }

    .tech-content {
      display: grid;
      grid-template-columns: 2fr 1fr;
      gap: 60px;
      margin-top: 50px;
    }

    .tech-text {
      h3 {
        color: #1a1a1a;
        font-size: 1.5rem;
        margin-bottom: 15px;
        margin-top: 30px;
        font-weight: 600;

        &:first-child {
          margin-top: 0;
        }
      }

      p {
        color: #666;
        line-height: 1.6;
        margin-bottom: 20px;
      }
    }

    .tech-features {
      background: #f8f9fa;
      padding: 40px 30px;
      border-radius: 15px;
      box-shadow: 0 10px 20px rgba(0, 0, 0, 0.05);

      ul {
        list-style: none;
        padding: 0;
        margin: 0;

        li {
          color: #555;
          margin-bottom: 15px;
          font-size: 1.1rem;

          &::before {
            content: '';
          }
        }
      }
    }
  }

  .cta {
    background: linear-gradient(135deg, #FF6B4A 0%, #ff8a6b 100%);
    padding: 80px 0;
    text-align: center;

    .cta-content {
      max-width: 700px;
      margin: 0 auto;

      h2 {
        color: white;
        font-size: 2.5rem;
        margin-bottom: 20px;
        font-weight: 600;
      }

      p {
        color: rgba(255, 255, 255, 0.9);
        font-size: 1.2rem;
        margin-bottom: 40px;
        line-height: 1.6;
      }
    }

    .cta-actions {
      display: flex;
      gap: 20px;
      justify-content: center;
      flex-wrap: wrap;
    }

    .btn-secondary {
      background: transparent;
      color: white;
      border: 2px solid white;

      &:hover {
        background: white;
        color: #FF6B4A;
      }
    }
  }
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.section-title {
  text-align: center;
  font-size: 2.5rem;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 20px;
}

.btn {
  display: inline-block;
  padding: 15px 30px;
  border-radius: 25px;
  text-decoration: none;
  font-weight: 600;
  transition: all 0.3s ease;

  &.btn-primary {
    background: white;
    color: #FF6B4A;

    &:hover {
      background: #f8f9fa;
      transform: translateY(-2px);
    }
  }

  &.btn-secondary {
    background: transparent;
    color: #FF6B4A;
    border: 2px solid #FF6B4A;

    &:hover {
      background: #FF6B4A;
      color: white;
    }
  }
}

@media (max-width: 768px) {
  .hero {
    .hero-container {
      grid-template-columns: 1fr;
      gap: 40px;
      text-align: center;
    }

    .hero-title {
      font-size: 2.5rem;
    }

    .hero-actions {
      justify-content: center;
      flex-wrap: wrap;
    }
  }

  .section-title {
    font-size: 2rem;
  }

  .e-words-section {
    padding: 15px 0;

    .e-words-carousel {
      max-width: 100%;
      padding: 0 20px;
    }

    .e-word-slide {
      padding: 10px 15px;
      min-height: 100px;

      h3 {
        font-size: 2.5rem;
        margin-bottom: 4px;
      }

      p {
        font-size: 1.4rem;
        line-height: 1.4;
      }
    }
  }

  .mission-content, .tech-content {
    grid-template-columns: 1fr;
    gap: 40px;
  }

  .cta-actions {
    flex-direction: column;
    align-items: center;
  }
}
</style>
